import type {
  ColumnDef,
  DateRangeFilterValue,
  DefaultSortState,
  FilterState,
  FilterValue,
  SortState,
} from '@/shared/types/table-filters.types';

/** Sentinel option value used by select/boolean filters to match null/empty data. */
export const EMPTY_FILTER_VALUE = 'empty';
/** Sentinel option value meaning "do not filter on this column". */
export const ANY_FILTER_VALUE = 'any';

export const DEFAULT_TABLE_SORT: DefaultSortState = {
  key: 'updatedAt',
  dir: 'desc',
  fallbackKey: 'createdAt',
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}(T[\d:.Z+-]+)?$/;
const END_OF_DAY_MS = 24 * 60 * 60 * 1000 - 1;

function isNullish(value: unknown): boolean {
  return value === null || value === undefined;
}

function isEmptyValue(value: unknown): boolean {
  return isNullish(value) || value === '';
}

function isDateRange(value: FilterValue): value is DateRangeFilterValue {
  return typeof value === 'object' && !Array.isArray(value);
}

function toTimestamp(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/** Timestamp for sorting — Date instances and ISO/date-only strings only, so text is never misread as a date. */
function toSortTimestamp(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string' || !ISO_DATETIME_PATTERN.test(value)) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

function boundTimestamp(bound: string | undefined, inclusiveEnd: boolean): number | null {
  if (!bound) return null;
  const time = toTimestamp(bound);
  if (time === null) return null;
  return inclusiveEnd && DATE_ONLY_PATTERN.test(bound) ? time + END_OF_DAY_MS : time;
}

function matchesText(value: unknown, filter: string): boolean {
  if (isNullish(value)) return false;
  return String(value).toLowerCase().includes(filter.toLowerCase());
}

function matchesSelect(value: unknown, filter: string): boolean {
  if (filter === EMPTY_FILTER_VALUE) return isEmptyValue(value);
  return !isNullish(value) && String(value) === filter;
}

function matchesBoolean(value: unknown, filter: string): boolean {
  if (filter === EMPTY_FILTER_VALUE) return isNullish(value);
  return value === (filter === 'true');
}

function matchesDateRange(value: unknown, range: DateRangeFilterValue): boolean {
  const time = toTimestamp(value);
  if (time === null) return false;
  const min = boundTimestamp(range.from, false);
  const max = boundTimestamp(range.to, true);
  if (min !== null && time < min) return false;
  return max === null || time <= max;
}

function matchesMultiSelect(value: unknown, selected: string[]): boolean {
  const values = Array.isArray(value) ? value : [value];
  return values.some((entry) => !isNullish(entry) && selected.includes(String(entry)));
}

/** True when the filter value would actually narrow the result set. */
export function isFilterActive(value: FilterValue | undefined): boolean {
  if (value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (isDateRange(value)) return Boolean(value.from ?? value.to);
  return value.trim() !== '' && value !== ANY_FILTER_VALUE;
}

export function countActiveFilters(filters: FilterState): number {
  return Object.values(filters).filter(isFilterActive).length;
}

function matchesColumn<T>(column: ColumnDef<T>, item: T, filter: FilterValue): boolean {
  const value = column.accessor(item);
  if (column.filterType === 'date-range') {
    return isDateRange(filter) ? matchesDateRange(value, filter) : true;
  }
  if (column.filterType === 'multi-select') {
    return Array.isArray(filter) ? matchesMultiSelect(value, filter) : true;
  }
  if (typeof filter !== 'string') return true;
  if (column.filterType === 'text') return matchesText(value, filter.trim());
  if (column.filterType === 'boolean') return matchesBoolean(value, filter);
  return matchesSelect(value, filter);
}

export function applyFilters<T>(data: T[], columns: ColumnDef<T>[], filters: FilterState): T[] {
  const active = Object.entries(filters).filter(([, value]) => isFilterActive(value));
  if (active.length === 0) return data;

  return data.filter((item) =>
    active.every(([key, value]) => {
      const column = columns.find((c) => c.key === key);
      return !column || matchesColumn(column, item, value as FilterValue);
    })
  );
}

/** Text columns are searched unless they opt out; other columns only when they opt in. */
function isSearchable<T>(column: ColumnDef<T>): boolean {
  return column.searchable ?? column.filterType === 'text';
}

/**
 * Narrows the rows to those whose searchable columns contain `term`, case-insensitively.
 * A blank term is a no-op, so the caller can always pipe the data through this.
 */
export function applySearch<T>(data: T[], columns: ColumnDef<T>[], term: string): T[] {
  const needle = term.trim().toLowerCase();
  if (needle === '') return data;

  const searched = columns.filter(isSearchable);
  return data.filter((item) =>
    searched.some((column) => {
      const value = column.accessor(item);
      return !isNullish(value) && String(value).toLowerCase().includes(needle);
    })
  );
}

function readProperty(item: unknown, key: string): unknown {
  if (item === null || typeof item !== 'object') return undefined;
  return (item as Record<string, unknown>)[key];
}

function sortValue<T>(item: T, key: string, columns: ColumnDef<T>[]): unknown {
  const column = columns.find((c) => c.key === key);
  if (!column) return readProperty(item, key);
  return (column.sortValue ?? column.accessor)(item);
}

function comparePresent(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  const aTime = toSortTimestamp(a);
  const bTime = toSortTimestamp(b);
  if (aTime !== null && bTime !== null) return aTime - bTime;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Sorts nullish values last in both directions, so empty rows never lead the table. */
function compareWithDirection(a: unknown, b: unknown, dir: SortState['dir']): number {
  if (isNullish(a) && isNullish(b)) return 0;
  if (isNullish(a)) return 1;
  if (isNullish(b)) return -1;
  return dir === 'asc' ? comparePresent(a, b) : comparePresent(b, a);
}

export function applySort<T>(
  data: T[],
  columns: ColumnDef<T>[],
  sort: SortState,
  fallbackKey?: string
): T[] {
  const resolve = (item: T): unknown => {
    const value = sortValue(item, sort.key, columns);
    if (!isNullish(value) || !fallbackKey) return value;
    return sortValue(item, fallbackKey, columns);
  };
  return [...data].sort((a, b) => compareWithDirection(resolve(a), resolve(b), sort.dir));
}

/** The search term, filters and sort a table view is currently showing. */
export interface TableQuery {
  searchTerm: string;
  filters: FilterState;
  sort: SortState | null;
  defaultSort: DefaultSortState;
}

/** Runs the full pipeline in order: search, then filters, then sort. */
export function applyTableQuery<T>(data: T[], columns: ColumnDef<T>[], query: TableQuery): T[] {
  const searched = applySearch(data, columns, query.searchTerm);
  const filtered = applyFilters(searched, columns, query.filters);
  const fallbackKey = query.sort ? undefined : query.defaultSort.fallbackKey;
  return applySort(filtered, columns, query.sort ?? query.defaultSort, fallbackKey);
}

/** Cycles a column through asc -> desc -> unsorted; switching columns restarts at asc. */
export function nextSort(current: SortState | null, key: string): SortState | null {
  if (current?.key !== key) return { key, dir: 'asc' };
  if (current.dir === 'asc') return { key, dir: 'desc' };
  return null;
}
